import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import Login from './Login';
import DayDetailsModal from './DayDetailsModal';
import './App.css';

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    pagerduty: true,
    pto: true,
    jira: true
  });
  const [platformEngOnly, setPlatformEngOnly] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);

  // Fetch events from backend
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/events');
      if (response.data.success) {
        setEvents(response.data.events);
        setError(null);
      } else {
        setError('Failed to fetch events');
      }
    } catch (err) {
      setError('Error connecting to server: ' + err.message);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and set up auto-refresh
  useEffect(() => {
    fetchEvents();
    
    // Auto-refresh every 10 minutes
    const interval = setInterval(fetchEvents, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchEvents]);

  // Platform Engineering team members
  const platformEngTeam = ['Dongju Won', 'Matt Merced', 'Isaac Peek', 'Alberto Fernandez'];

  // Filter events based on selected sources
  useEffect(() => {
    const filtered = events.filter(event => {
      // Check source filter
      if (!filters[event.source]) return false;
      
      // Platform Engineering only filter for PTO
      if (platformEngOnly && event.source === 'pto') {
        const eventTitle = event.title.toLowerCase();
        const isTeamMember = platformEngTeam.some(member => 
          eventTitle.includes(member.toLowerCase())
        );
        if (!isTeamMember) return false;
      }
      
      return true;
    });
    setFilteredEvents(filtered);
  }, [events, filters, platformEngOnly]);

  // Handle filter checkbox changes
  const handleFilterChange = (source) => {
    setFilters(prev => ({
      ...prev,
      [source]: !prev[source]
    }));
  };

  // Handle date click
  const handleDateClick = (dateClickInfo) => {
    const clickedDate = dateClickInfo.dateStr;
    const dayEvents = filteredEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = event.end ? new Date(event.end) : eventStart;
      
      // Parse the clicked date as local date (not UTC)
      const [year, month, day] = clickedDate.split('-').map(num => parseInt(num));
      const clickedDateTime = new Date(year, month - 1, day);
      
      // Get local date strings for comparison
      const eventStartDate = new Date(eventStart.getFullYear(), eventStart.getMonth(), eventStart.getDate());
      const eventEndDate = new Date(eventEnd.getFullYear(), eventEnd.getMonth(), eventEnd.getDate());
      const clickedDateOnly = new Date(clickedDateTime.getFullYear(), clickedDateTime.getMonth(), clickedDateTime.getDate());
      
      // Check if the clicked date falls within the event's date range
      return eventStartDate.getTime() === clickedDateOnly.getTime() ||
             eventEndDate.getTime() === clickedDateOnly.getTime() ||
             (eventStartDate < clickedDateOnly && eventEndDate > clickedDateOnly);
    });
    
    setSelectedDate(clickedDate);
    setSelectedDateEvents(dayEvents);
    setShowDayModal(true);
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    if (event.extendedProps?.url) {
      window.open(event.extendedProps.url, '_blank');
    }
  };

  if (!isAuthenticated) {
    return <Login />;
  }

  const user = accounts[0];

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <img 
                src="/images/tactical-logo-trimmed.png" 
                alt="TactiCal Logo" 
                style={{ height: '90px', marginRight: '20px' }}
              />
              <h1 className="mb-0">Platform Engineering Calendar</h1>
            </div>
            <div className="text-end">
              <span className="me-3">Welcome, {user?.name || user?.username}</span>
              <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>
                Sign Out
              </button>
            </div>
          </div>
          
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="filter-controls">
            <h5>Filter Events:</h5>
            <div className="form-check form-check-inline filter-checkbox">
              <input
                className="form-check-input"
                type="checkbox"
                id="pagerdutyFilter"
                checked={filters.pagerduty}
                onChange={() => handleFilterChange('pagerduty')}
              />
              <label className="form-check-label" htmlFor="pagerdutyFilter">
                <span style={{color: '#f44336'}}>■</span> PagerDuty On-Call
              </label>
            </div>
            <div className="form-check form-check-inline filter-checkbox">
              <input
                className="form-check-input"
                type="checkbox"
                id="ptoFilter"
                checked={filters.pto}
                onChange={() => handleFilterChange('pto')}
              />
              <label className="form-check-label" htmlFor="ptoFilter">
                <span style={{color: '#2196f3'}}>■</span> PTO (Google Calendar)
              </label>
            </div>
            <div className="form-check form-check-inline filter-checkbox">
              <input
                className="form-check-input"
                type="checkbox"
                id="jiraFilter"
                checked={filters.jira}
                onChange={() => handleFilterChange('jira')}
              />
              <label className="form-check-label" htmlFor="jiraFilter">
                <span style={{color: '#4caf50'}}>■</span> Jira Change Requests
              </label>
            </div>
            <div className="form-check form-check-inline filter-checkbox ms-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="platformEngFilter"
                checked={platformEngOnly}
                onChange={() => setPlatformEngOnly(!platformEngOnly)}
              />
              <label className="form-check-label" htmlFor="platformEngFilter">
                <strong>Platform Engineering PTO Only</strong>
              </label>
            </div>
            <button 
              className="btn btn-sm btn-outline-primary float-end"
              onClick={fetchEvents}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Now'}
            </button>
          </div>

          <div className="calendar-container">
            {loading && events.length === 0 ? (
              <div className="text-center p-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                events={filteredEvents}
                height="auto"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,dayGridWeek'
                }}
                eventDisplay="block"
                displayEventTime={true}
                displayEventEnd={true}
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short'
                }}
                eventColor="#378006"
                nextDayThreshold="00:00:00"
                dateClick={handleDateClick}
                eventClick={handleEventClick}
              />
            )}
          </div>
        </div>
      </div>
      
      <DayDetailsModal
        show={showDayModal}
        onHide={() => setShowDayModal(false)}
        date={selectedDate}
        events={selectedDateEvents}
      />
    </div>
  );
}

export default App;